// VacatairePayrollPage - Management page for vacataire (part-time) teacher payments based on completed sessions.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Users,
  Clock,
  CheckCircle,
  Download,
  X,
  Loader2,
  AlertCircle,
  CreditCard,
  Building2,
  History,
  CheckCheck,
  MapPin,
  Plus,
  Minus,
  ChevronDown,
  ChevronRight,
  Calendar,
  Banknote,
  FileText,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  getVacatairePayrollTable,
  generateVacataireBankAuthorization,
  getVacataireBankAuthorizationHistory,
  downloadVacataireBankAuthorizationPdf,
  getInstitutionBanks,
  confirmVacataireBankAuthorizationPayment,
  createVacataireAdjustment,
} from '@/services/payrollService';
import type {
  VacatairePayrollEntry,
  VacatairePayrollTableResponse,
  VacataireBankAuthorization,
  InstitutionBankInfo,
} from '@/types/payroll.types';

// ==================== CONSTANTS ====================

const MONTH_NAMES = [
  'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre',
];

const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

// ==================== HELPERS ====================

function formatCurrency(amount: number): string {
  return amount.toLocaleString('fr-FR') + ' MAD';
}

// ==================== MODAL BACKDROP ====================

function ModalBackdrop({
  children,
  onClose,
  size = 'md',
}: {
  children: React.ReactNode;
  onClose: () => void;
  size?: 'md' | 'lg' | 'xl' | 'full';
}) {
  const sizeClass = {
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    full: 'max-w-6xl',
  }[size];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full overflow-hidden ${sizeClass} max-h-[90vh] flex flex-col`}>
        {children}
      </div>
    </div>
  );
}

// ==================== ADJUSTMENT MODAL ====================

function AdjustmentModal({ entry, month, year, onClose, onSuccess }: {
  entry: VacatairePayrollEntry; month: number; year: number; onClose: () => void; onSuccess: () => void;
}) {
  const [adjustmentType, setAdjustmentType] = useState<'DEDUCTION' | 'BONUS'>('DEDUCTION');
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reason.trim()) { setError('Le motif est obligatoire.'); return; }
    if (amount <= 0) { setError('Le montant doit etre superieur a 0.'); return; }
    setSubmitting(true); setError(null);
    try {
      await createVacataireAdjustment({ userId: entry.userId, month, year, adjustmentType, amount, reason: reason.trim() });
      onSuccess();
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erreur'); }
    finally { setSubmitting(false); }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="px-6 pt-6 pb-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Ajustement de Paie - Vacataire</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">{entry.firstName} {entry.lastName} - {MONTH_NAMES[month - 1]} {year}</p>
      </div>
      <div className="px-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={adjustmentType === 'DEDUCTION'} onChange={() => setAdjustmentType('DEDUCTION')} className="text-red-600" />
              <Minus size={14} className="text-red-500" /><span className="text-sm">Retenue</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={adjustmentType === 'BONUS'} onChange={() => setAdjustmentType('BONUS')} className="text-green-600" />
              <Plus size={14} className="text-green-500" /><span className="text-sm">Prime</span>
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Montant (MAD)</label>
          <input type="number" min={0} step={0.01} value={amount || ''} onChange={(e) => setAmount(Number(e.target.value))}
            placeholder="0.00" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Motif *</label>
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Indiquez le motif..." className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm resize-none" />
        </div>
        {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
      </div>
      <div className="px-6 py-4 flex justify-end gap-3">
        <button onClick={onClose} disabled={submitting} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Annuler</button>
        <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2">
          {submitting && <Loader2 size={14} className="animate-spin" />}Enregistrer
        </button>
      </div>
    </ModalBackdrop>
  );
}

// ==================== BANK AUTHORIZATION MODAL (CAMPUS-BASED) ====================

function BankAuthorizationModal({
  month,
  year,
  entries,
  campuses,
  selectedCampusId: initialCampusId,
  onClose,
}: {
  month: number;
  year: number;
  entries: VacatairePayrollEntry[];
  campuses: { id: string; name: string }[];
  selectedCampusId: string;
  onClose: () => void;
}) {
  const [banks, setBanks] = useState<InstitutionBankInfo[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  const [selectedCampusId, setSelectedCampusId] = useState<string>(initialCampusId);
  const [generateAllMode, setGenerateAllMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedAuth, setGeneratedAuth] = useState<VacataireBankAuthorization | null>(null);
  const [generatedAllAuths, setGeneratedAllAuths] = useState<VacataireBankAuthorization[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState<{ current: number; total: number } | null>(null);

  // Group entries by campus (using campusId from session, not user)
  const campusGroups = React.useMemo(() => {
    const groups: Record<string, { campus: { id: string; name: string } | null; entries: VacatairePayrollEntry[]; total: number; totalHours: number; count: number }> = {};
    entries.forEach((e) => {
      const key = e.campusId || 'unassigned';
      if (!groups[key]) {
        // Use campusName from entry (comes from backend, based on session's campus)
        const campus = e.campusId ? { id: e.campusId, name: e.campusName || 'Campus' } : null;
        groups[key] = { campus, entries: [], total: 0, totalHours: 0, count: 0 };
      }
      if (e.totalAmount > 0) {
        groups[key].entries.push(e);
        groups[key].total += e.totalAmount;
        groups[key].totalHours += e.totalHours;
        groups[key].count++;
      }
    });
    return groups;
  }, [entries]);

  // Filtered entries based on selected campus
  const filteredEntries = React.useMemo(() => {
    if (selectedCampusId) {
      return entries.filter((e) => e.campusId === selectedCampusId && e.totalAmount > 0);
    }
    return entries.filter((e) => e.totalAmount > 0);
  }, [entries, selectedCampusId]);

  const totalAmount = filteredEntries.reduce((sum, e) => sum + e.totalAmount, 0);
  const totalHours = filteredEntries.reduce((sum, e) => sum + e.totalHours, 0);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getInstitutionBanks();
        setBanks(data.filter((b) => b.isActive));
        autoSelectBank(data.filter((b) => b.isActive), selectedCampusId);
      } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erreur'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const autoSelectBank = (bankList: InstitutionBankInfo[], campusId: string) => {
    if (campusId) {
      const campusBank = bankList.find((b) => b.campusIds?.includes(campusId));
      if (campusBank) {
        setSelectedBankId(campusBank.id);
        return;
      }
    }
    const primary = bankList.find((b) => b.isPrimary);
    if (primary) setSelectedBankId(primary.id);
    else if (bankList.length > 0) setSelectedBankId(bankList[0].id);
  };

  const handleCampusChange = (campusId: string) => {
    setSelectedCampusId(campusId);
    setGenerateAllMode(false);
    setGeneratedAuth(null);
    setGeneratedAllAuths([]);
    setError(null);
    autoSelectBank(banks, campusId);
  };

  const handleSelectAllCampuses = () => {
    setGenerateAllMode(true);
    setSelectedCampusId('');
    setGeneratedAuth(null);
    setGeneratedAllAuths([]);
    setError(null);
  };

  const handleGenerateAll = async () => {
    if (banks.length === 0) { setError('Aucune banque configuree.'); return; }
    setSubmitting(true); setError(null);
    const results: VacataireBankAuthorization[] = [];
    setGeneratingProgress({ current: 0, total: campuses.length });

    for (let i = 0; i < campuses.length; i++) {
      const campus = campuses[i];
      const group = campusGroups[campus.id];
      if (!group || group.count === 0) {
        setGeneratingProgress({ current: i + 1, total: campuses.length });
        continue;
      }

      const campusBank = banks.find((b) => b.campusIds?.includes(campus.id));
      const bankToUse = campusBank || banks.find((b) => b.isPrimary) || banks[0];

      try {
        const result = await generateVacataireBankAuthorization({
          month,
          year,
          institutionBankInfoId: bankToUse.id,
          campusId: campus.id,
        });
        results.push(result);
      } catch (err: unknown) {
        console.error(`Error generating for ${campus.name}:`, err);
      }
      setGeneratingProgress({ current: i + 1, total: campuses.length });
    }

    setGeneratedAllAuths(results);
    setGeneratingProgress(null);
    setSubmitting(false);
  };

  const handleGenerate = async () => {
    if (!selectedBankId) { setError('Veuillez selectionner une banque.'); return; }
    if (!selectedCampusId) { setError('Veuillez selectionner un campus.'); return; }
    setSubmitting(true); setError(null);
    try {
      const result = await generateVacataireBankAuthorization({
        month,
        year,
        institutionBankInfoId: selectedBankId,
        campusId: selectedCampusId || undefined,
      });
      setGeneratedAuth(result);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    if (!generatedAuth) return;
    setDownloading(true);
    try {
      const blob = await downloadVacataireBankAuthorizationPdf(generatedAuth.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ordre-virement-vacataires-${MONTH_NAMES[month - 1]}-${year}-v${generatedAuth.version}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erreur'); }
    finally { setDownloading(false); }
  };

  const selectedBank = banks.find((b) => b.id === selectedBankId);

  return (
    <ModalBackdrop onClose={onClose} size="xl">
      {/* Header */}
      <div className="px-6 pt-5 pb-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Generer Ordre de Virement - Vacataires</h3>
            <p className="text-sm text-gray-500 mt-0.5">Periode: <strong>{MONTH_NAMES[month - 1]} {year}</strong></p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-400" /></button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-500 animate-spin" /></div>
        ) : generatedAuth ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
            <CheckCircle className="mx-auto h-10 w-10 text-emerald-500 mb-3" />
            <p className="text-base font-medium text-emerald-800 mb-1">Autorisation generee avec succes</p>
            <p className="text-sm text-emerald-600 mb-4">Version {generatedAuth.version} - {generatedAuth.staffCount} vacataires - {formatCurrency(generatedAuth.totalAmount)}</p>
            <button onClick={handleDownload} disabled={downloading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}Telecharger le PDF
            </button>
          </div>
        ) : generatedAllAuths.length > 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
            <div className="text-center mb-4">
              <CheckCircle className="mx-auto h-10 w-10 text-emerald-500 mb-3" />
              <p className="text-base font-medium text-emerald-800 mb-1">{generatedAllAuths.length} autorisation{generatedAllAuths.length > 1 ? 's' : ''} generee{generatedAllAuths.length > 1 ? 's' : ''}</p>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {generatedAllAuths.map((auth) => (
                <div key={auth.id} className="flex items-center justify-between bg-white rounded-lg border border-emerald-200 p-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{auth.campusName || 'Campus'}</p>
                    <p className="text-xs text-gray-500">v{auth.version} - {auth.staffCount} vac. - {auth.totalHours?.toFixed(2) || 0}h - {formatCurrency(auth.totalAmount)}</p>
                  </div>
                  <button onClick={() => {
                    downloadVacataireBankAuthorizationPdf(auth.id).then((blob) => {
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `ordre-virement-vacataires-${auth.campusName || 'campus'}-${MONTH_NAMES[month - 1]}-${year}-v${auth.version}.pdf`;
                      document.body.appendChild(link); link.click(); document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    });
                  }} className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700">
                    <Download size={12} /> PDF
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : generatingProgress ? (
          <div className="text-center py-8">
            <Loader2 className="mx-auto h-8 w-8 text-emerald-500 animate-spin mb-3" />
            <p className="text-sm text-gray-600">Generation en cours...</p>
            <p className="text-lg font-semibold text-emerald-600 mt-1">{generatingProgress.current} / {generatingProgress.total}</p>
          </div>
        ) : (
          <>
            {/* Step 1: Campus Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <MapPin size={14} className="text-emerald-500" /> Selectionner le campus
              </label>
              {campuses.length <= 1 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-800 font-medium">{campuses[0]?.name || 'Campus principal'}</p>
                  <p className="text-xs text-emerald-600 mt-0.5">{filteredEntries.length} vacataires - {totalHours.toFixed(2)}h - {formatCurrency(totalAmount)}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* All campuses option */}
                  <button onClick={handleSelectAllCampuses}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                      generateAllMode
                        ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-200'
                        : 'border-gray-200 hover:border-teal-300 hover:bg-gray-50'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className={generateAllMode ? 'text-teal-600' : 'text-gray-400'} />
                        <span className={`text-sm font-semibold ${generateAllMode ? 'text-teal-800' : 'text-gray-800'}`}>
                          Tous les campus
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-medium">
                          {campuses.length} PDFs
                        </span>
                      </div>
                      <span className={`text-sm font-bold ${generateAllMode ? 'text-teal-700' : 'text-gray-700'}`}>
                        {formatCurrency(Object.values(campusGroups).reduce((sum, g) => sum + (g?.total || 0), 0))}
                      </span>
                    </div>
                  </button>

                  {/* Individual campus options */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                    {campuses.map((campus) => {
                      const group = campusGroups[campus.id];
                      const isSelected = selectedCampusId === campus.id && !generateAllMode;
                      return (
                        <button key={campus.id} onClick={() => handleCampusChange(campus.id)}
                          className={`text-left p-3 rounded-xl border-2 transition-all ${
                            isSelected
                              ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200'
                              : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                          }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin size={14} className={isSelected ? 'text-emerald-600' : 'text-gray-400'} />
                            <span className={`text-sm font-medium ${isSelected ? 'text-emerald-800' : 'text-gray-800'}`}>{campus.name}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className={isSelected ? 'text-emerald-600' : 'text-gray-500'}>{group?.count || 0} vac.</span>
                            <span className={isSelected ? 'text-emerald-600' : 'text-gray-500'}>{(group?.totalHours || 0).toFixed(1)}h</span>
                            <span className={`font-medium ${isSelected ? 'text-emerald-700' : 'text-gray-700'}`}>{formatCurrency(group?.total || 0)}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Summary & Bank */}
            {selectedCampusId && (
              <>
                {/* Summary */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-emerald-700">{filteredEntries.length}</p>
                      <p className="text-xs text-emerald-600">Vacataires</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-700">{totalHours.toFixed(2)}h</p>
                      <p className="text-xs text-emerald-600">Total heures</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalAmount)}</p>
                      <p className="text-xs text-emerald-600">Montant total</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-400">
                        {entries.filter((e) => (selectedCampusId ? e.campusId === selectedCampusId : true) && e.totalAmount <= 0).length}
                      </p>
                      <p className="text-xs text-gray-500">Exclus (0 MAD)</p>
                    </div>
                  </div>
                </div>

                {/* Bank info (auto-selected) */}
                {selectedBank && (
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Compte bancaire:</span>
                        <span className="text-sm text-gray-900">{selectedBank.bankName}</span>
                        {selectedBank.campusIds?.includes(selectedCampusId) && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Campus</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 font-mono">{selectedBank.rib}</span>
                    </div>
                  </div>
                )}

                {/* Staff preview */}
                {filteredEntries.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                      <Users size={14} className="text-emerald-500" /> Vacataires inclus ({filteredEntries.length})
                    </label>
                    <div className="border border-gray-200 rounded-xl overflow-hidden max-h-44 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-1.5 text-left font-medium text-gray-500">Nom</th>
                            <th className="px-3 py-1.5 text-left font-medium text-gray-500">CIN</th>
                            <th className="px-3 py-1.5 text-left font-medium text-gray-500">RIB</th>
                            <th className="px-3 py-1.5 text-center font-medium text-gray-500">Heures</th>
                            <th className="px-3 py-1.5 text-right font-medium text-gray-500">Montant</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filteredEntries.map((e) => (
                            <tr key={e.userId} className="hover:bg-emerald-50/30">
                              <td className="px-3 py-1.5 text-gray-900 font-medium">{e.lastName} {e.firstName}</td>
                              <td className="px-3 py-1.5 text-gray-600">{e.cin || '-'}</td>
                              <td className="px-3 py-1.5 text-gray-600 font-mono">{e.rib || '-'}</td>
                              <td className="px-3 py-1.5 text-center text-gray-700">{e.totalHours.toFixed(2)}h</td>
                              <td className="px-3 py-1.5 text-right font-medium text-gray-900">{formatCurrency(e.totalAmount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-gray-100 flex justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Fermer</button>
        {!generatedAuth && !generatedAllAuths.length && !generatingProgress && selectedCampusId && !generateAllMode && (
          <button onClick={handleGenerate} disabled={submitting || banks.length === 0 || filteredEntries.length === 0}
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl text-sm font-medium hover:from-emerald-600 hover:to-teal-700 disabled:opacity-60 flex items-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            <CreditCard size={14} /> Generer le PDF
          </button>
        )}
        {!generatedAuth && !generatedAllAuths.length && !generatingProgress && generateAllMode && (
          <button onClick={handleGenerateAll} disabled={submitting || banks.length === 0}
            className="px-5 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl text-sm font-medium hover:from-teal-600 hover:to-emerald-700 disabled:opacity-60 flex items-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            <CreditCard size={14} /> Generer tous les PDFs
          </button>
        )}
      </div>
    </ModalBackdrop>
  );
}

// ==================== AUTHORIZATION HISTORY MODAL (YEAR-BASED WITH CAMPUS GROUPING) ====================

function AuthorizationHistoryModal({ canPayVacataires, onClose }: { canPayVacataires: boolean; onClose: () => void }) {
  const now = new Date();
  const [histYear, setHistYear] = useState<number>(now.getFullYear());
  const [history, setHistory] = useState<VacataireBankAuthorization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(`${now.getMonth() + 1}-${now.getFullYear()}`);
  const [expandedCampus, setExpandedCampus] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const allHistory: VacataireBankAuthorization[] = [];
      const promises = Array.from({ length: 12 }, (_, i) =>
        getVacataireBankAuthorizationHistory(i + 1, histYear).catch(() => [] as VacataireBankAuthorization[])
      );
      const results = await Promise.all(promises);
      results.forEach((monthData) => allHistory.push(...monthData));
      setHistory(allHistory);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erreur'); }
    finally { setLoading(false); }
  }, [histYear]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleConfirmPayment = async (auth: VacataireBankAuthorization) => {
    setConfirmingId(auth.id);
    try {
      await confirmVacataireBankAuthorizationPayment(auth.id);
      await loadHistory();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la confirmation');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleDownload = async (auth: VacataireBankAuthorization) => {
    setDownloadingId(auth.id);
    try {
      const blob = await downloadVacataireBankAuthorizationPdf(auth.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const campusSlug = auth.campusName ? `-${auth.campusName.replace(/\s+/g, '-')}` : '';
      link.download = `ordre-virement-vacataires${campusSlug}-${MONTH_NAMES[auth.month - 1]}-${auth.year}-v${auth.version}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erreur'); }
    finally { setDownloadingId(null); }
  };

  // Group history by month, then by campus within each month
  const groupedByMonth = history.reduce<Record<string, VacataireBankAuthorization[]>>((acc, auth) => {
    const key = `${auth.month}-${auth.year}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(auth);
    return acc;
  }, {});

  // Group by campus within month - only include entries with valid campusId
  const groupByMonthAndCampus = (auths: VacataireBankAuthorization[]) => {
    const byCampus: Record<string, VacataireBankAuthorization[]> = {};
    auths.forEach((auth) => {
      if (!auth.campusId || !auth.campusName) return;
      const campusKey = auth.campusId;
      if (!byCampus[campusKey]) byCampus[campusKey] = [];
      byCampus[campusKey].push(auth);
    });
    // Sort each campus group by version descending (latest first)
    Object.values(byCampus).forEach((arr) => arr.sort((a, b) => b.version - a.version));
    return byCampus;
  };

  // All 12 months for the selected year
  const allMonths = Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    const key = `${m}-${histYear}`;
    const monthAuths = groupedByMonth[key] || [];
    const byCampus = groupByMonthAndCampus(monthAuths);
    const totalStaff = Object.values(byCampus).reduce((sum, arr) => sum + (arr[0]?.staffCount || 0), 0);
    const totalAmt = Object.values(byCampus).reduce((sum, arr) => sum + (arr[0]?.totalAmount || 0), 0);
    const totalHrs = Object.values(byCampus).reduce((sum, arr) => sum + (arr[0]?.totalHours || 0), 0);
    const campusCount = Object.keys(byCampus).length;
    const paidCount = Object.values(byCampus).filter((arr) => arr[0]?.isPaid).length;
    const paymentStatus: 'paid' | 'partial' | 'pending' | 'none' =
      campusCount === 0 ? 'none' :
      paidCount === campusCount ? 'paid' :
      paidCount > 0 ? 'partial' : 'pending';
    return { month: m, year: histYear, key, auths: monthAuths, byCampus, totalStaff, totalAmount: totalAmt, totalHours: totalHrs, paymentStatus, paidCount, campusCount };
  });

  const totalAmount = allMonths.reduce((sum, m) => sum + m.totalAmount, 0);

  return (
    <ModalBackdrop onClose={onClose} size="full">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <History className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Historique des Ordres de Virement - Vacataires</h3>
              <p className="text-sm text-gray-500">Toutes les autorisations generees par mois</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-400" /></button>
        </div>

        {/* Year filter + stats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Calendar size={14} className="text-gray-400" />
              <label className="text-sm font-medium text-gray-600">Annee:</label>
            </div>
            <select value={histYear} onChange={(e) => setHistYear(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
              {YEARS.map((yr) => (<option key={yr} value={yr}>{yr}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <Banknote size={14} className="text-emerald-500" />
            <span className="font-medium text-gray-800">{formatCurrency(totalAmount)}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 text-emerald-500 animate-spin" /></div>
        ) : error ? (
          <p className="text-sm text-red-600 flex items-center gap-1 py-4"><AlertCircle size={14} />{error}</p>
        ) : (
          <div className="space-y-2">
            {allMonths.map(({ month: m, key, byCampus, totalStaff, totalAmount: monthTotal, totalHours: monthHours, paymentStatus, paidCount, campusCount }) => {
              const isExpanded = expandedMonth === key;
              const hasData = campusCount > 0;

              const statusBadge = paymentStatus === 'paid' ? (
                <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Paye</span>
              ) : paymentStatus === 'partial' ? (
                <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium">Partiellement paye ({paidCount}/{campusCount})</span>
              ) : paymentStatus === 'pending' ? (
                <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">En attente</span>
              ) : null;

              return (
                <div key={key} className={`border rounded-xl overflow-hidden transition-all ${
                  hasData ? 'border-gray-200' : 'border-gray-100 opacity-50'
                }`}>
                  <button
                    onClick={() => hasData && setExpandedMonth(isExpanded ? null : key)}
                    disabled={!hasData}
                    className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                      hasData ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                    } ${isExpanded ? 'bg-gray-50' : ''}`}>
                    <div className="flex items-center gap-3">
                      {hasData ? (
                        isExpanded ? <ChevronDown size={16} className="text-emerald-500" /> : <ChevronRight size={16} className="text-gray-400" />
                      ) : <div className="w-4" />}
                      <span className="font-medium text-gray-900">{MONTH_NAMES[m - 1]}</span>
                      {statusBadge}
                      {hasData && campusCount > 0 && (
                        <span className="text-xs text-gray-400">{campusCount} campus</span>
                      )}
                    </div>
                    {hasData ? (
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-500">{totalStaff} vac.</span>
                        <span className="text-gray-500">{monthHours.toFixed(1)}h</span>
                        <span className="font-semibold text-gray-900 min-w-[100px] text-right">{formatCurrency(monthTotal)}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Aucune autorisation</span>
                    )}
                  </button>

                  {isExpanded && hasData && (
                    <div className="border-t border-gray-200 divide-y divide-gray-100">
                      {Object.entries(byCampus).map(([campusKey, campusAuths]) => {
                        const campusName = campusAuths[0]?.campusName || 'Campus';
                        const latestVersion = campusAuths[0];
                        const olderVersions = campusAuths.slice(1);
                        const isCampusExpanded = expandedCampus === `${key}-${campusKey}`;
                        const isPaid = latestVersion.isPaid;

                        return (
                          <div key={campusKey} className="bg-white">
                            {/* Campus header with latest version */}
                            <div className={`px-4 py-3 flex items-center justify-between ${isPaid ? 'bg-gradient-to-r from-emerald-50/50 to-white' : 'bg-gradient-to-r from-teal-50/50 to-white'}`}>
                              <div className="flex items-center gap-3">
                                <MapPin size={14} className={isPaid ? 'text-emerald-500' : 'text-teal-500'} />
                                <span className="text-sm font-semibold text-gray-800">{campusName}</span>
                                <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
                                  v{latestVersion.version}
                                </span>
                                {isPaid ? (
                                  <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Paye</span>
                                ) : (
                                  <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium">En attente</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-500">{latestVersion.staffCount} vac.</span>
                                <span className="text-sm text-gray-500">{(latestVersion.totalHours || 0).toFixed(1)}h</span>
                                <span className="text-sm font-semibold text-gray-900">{formatCurrency(latestVersion.totalAmount)}</span>
                                <button onClick={() => handleDownload(latestVersion)} disabled={downloadingId === latestVersion.id}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50">
                                  {downloadingId === latestVersion.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                  PDF
                                </button>
                                {canPayVacataires && !isPaid && (
                                  <button onClick={() => handleConfirmPayment(latestVersion)} disabled={confirmingId === latestVersion.id}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
                                    {confirmingId === latestVersion.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                                    Confirmer
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Older versions expandable */}
                            {olderVersions.length > 0 && (
                              <div className="border-t border-gray-100">
                                <button
                                  onClick={() => setExpandedCampus(isCampusExpanded ? null : `${key}-${campusKey}`)}
                                  className="w-full px-4 py-2 text-left text-xs text-gray-500 hover:bg-gray-50 flex items-center gap-2">
                                  {isCampusExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  {olderVersions.length} version{olderVersions.length > 1 ? 's' : ''} anterieure{olderVersions.length > 1 ? 's' : ''}
                                </button>
                                {isCampusExpanded && (
                                  <div className="bg-gray-50/50 divide-y divide-gray-100">
                                    {olderVersions.map((auth) => (
                                      <div key={auth.id} className="px-4 py-2 flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-3">
                                          <span className="inline-flex items-center justify-center h-5 w-7 rounded text-[10px] font-bold bg-gray-100 text-gray-500">
                                            v{auth.version}
                                          </span>
                                          <span className="text-gray-600">
                                            {new Date(auth.generatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                            <span className="text-gray-400 ml-1 text-xs">
                                              {new Date(auth.generatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          <span className="text-gray-500">{auth.staffCount} vac.</span>
                                          <span className="text-gray-500">{(auth.totalHours || 0).toFixed(1)}h</span>
                                          <span className="font-medium text-gray-700">{formatCurrency(auth.totalAmount)}</span>
                                          <button onClick={() => handleDownload(auth)} disabled={downloadingId === auth.id}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded disabled:opacity-50">
                                            {downloadingId === auth.id ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                                            PDF
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
        <button onClick={onClose} className="px-5 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Fermer</button>
      </div>
    </ModalBackdrop>
  );
}

// ==================== BULK CONFIRM MODAL (CAMPUS-BASED) ====================

function BulkConfirmModal({ month, year, campuses, onClose, onSuccess }: {
  month: number; year: number; campuses: { id: string; name: string }[]; onClose: () => void; onSuccess: () => void;
}) {
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');
  const [authorizations, setAuthorizations] = useState<VacataireBankAuthorization[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load authorizations for this month to show status
  useEffect(() => {
    const loadAuths = async () => {
      setLoading(true);
      try {
        const data = await getVacataireBankAuthorizationHistory(month, year);
        setAuthorizations(data);
      } catch (err) {
        console.error('Error loading authorizations:', err);
      } finally {
        setLoading(false);
      }
    };
    loadAuths();
  }, [month, year]);

  // Group by campus and get latest version
  const campusAuths = React.useMemo(() => {
    const byCampus: Record<string, VacataireBankAuthorization> = {};
    authorizations.forEach((auth) => {
      if (!auth.campusId) return;
      if (!byCampus[auth.campusId] || auth.version > byCampus[auth.campusId].version) {
        byCampus[auth.campusId] = auth;
      }
    });
    return byCampus;
  }, [authorizations]);

  // Filter by selected campus
  const filteredAuths = selectedCampusId
    ? Object.values(campusAuths).filter((a) => a.campusId === selectedCampusId)
    : Object.values(campusAuths);

  const pendingAuths = filteredAuths.filter((a) => !a.isPaid);
  const paidAuths = filteredAuths.filter((a) => a.isPaid);

  const handleConfirmAll = async () => {
    if (pendingAuths.length === 0) {
      setSuccessMessage('Toutes les autorisations sont deja confirmees.');
      return;
    }
    setSubmitting(true); setError(null); setSuccessMessage(null);
    try {
      // Confirm all pending authorizations
      for (const auth of pendingAuths) {
        await confirmVacataireBankAuthorizationPayment(auth.id);
      }
      setSuccessMessage(`${pendingAuths.length} autorisation${pendingAuths.length > 1 ? 's' : ''} confirmee${pendingAuths.length > 1 ? 's' : ''} avec succes.`);
      setTimeout(() => onSuccess(), 1500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la confirmation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose} size="lg">
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
            <CheckCheck className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Confirmation de Paiement - Vacataires</h3>
            <p className="text-sm text-gray-500">{MONTH_NAMES[month - 1]} {year}</p>
          </div>
        </div>
      </div>
      <div className="px-6 space-y-4 overflow-y-auto max-h-[50vh]">
        {campuses.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filtrer par campus</label>
            <select value={selectedCampusId} onChange={(e) => setSelectedCampusId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500">
              <option value="">Tous les campus</option>
              {campuses.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 text-emerald-500 animate-spin" />
          </div>
        ) : filteredAuths.length === 0 ? (
          <div className="text-center py-6">
            <AlertCircle className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">Aucune autorisation trouvee pour cette periode.</p>
            <p className="text-xs text-gray-400 mt-1">Generez d'abord un ordre de virement.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-500">Statut des autorisations:</span>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">{paidAuths.length} paye{paidAuths.length > 1 ? 's' : ''}</span>
                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">{pendingAuths.length} en attente</span>
              </div>
            </div>
            {filteredAuths.map((auth) => (
              <div key={auth.id} className={`flex items-center justify-between p-3 rounded-lg border ${auth.isPaid ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <MapPin size={14} className={auth.isPaid ? 'text-emerald-500' : 'text-gray-400'} />
                  <div>
                    <span className="text-sm font-medium text-gray-900">{auth.campusName}</span>
                    <span className="text-xs text-gray-400 ml-2">v{auth.version}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">{auth.staffCount} vac.</span>
                  <span className="text-sm text-gray-600">{(auth.totalHours || 0).toFixed(1)}h</span>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(auth.totalAmount)}</span>
                  {auth.isPaid ? (
                    <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Paye</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium">En attente</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{error}</p>}
        {successMessage && (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <CheckCircle size={16} className="text-emerald-600" />
            <span className="text-sm text-emerald-700">{successMessage}</span>
          </div>
        )}
      </div>
      <div className="px-6 py-4 flex gap-3 border-t border-gray-100">
        <button onClick={onClose} disabled={submitting} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Fermer</button>
        {!successMessage && pendingAuths.length > 0 && (
          <button onClick={handleConfirmAll} disabled={submitting} className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Confirmer {pendingAuths.length > 1 ? `les ${pendingAuths.length} paiements` : 'le paiement'}
          </button>
        )}
      </div>
    </ModalBackdrop>
  );
}

// ==================== MAIN COMPONENT ====================

export default function VacatairePayrollPage() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState<number>(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(now.getFullYear());

  // Permissions
  const { hasPermission } = usePermissions();
  const canManageVacataires = hasPermission(Permission.PAYROLL_VACATAIRE_MANAGE);
  const canGenerateBankAuth = hasPermission(Permission.PAYROLL_VACATAIRE_BANK_AUTH);
  // Aliases for backward compatibility
  const canValidateHours = canManageVacataires;
  const canPayVacataires = canManageVacataires;

  interface Campus { id: string; name: string; isActive: boolean; }
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');

  const [entries, setEntries] = useState<VacatairePayrollEntry[]>([]);
  const [summary, setSummary] = useState<VacatairePayrollTableResponse['summary']>({
    totalVacataires: 0, totalHours: 0, totalAmount: 0, pendingCount: 0, paidCount: 0,
  });
  const [authSummary, setAuthSummary] = useState<{ pending: number; paid: number; total: number }>({
    pending: 0, paid: 0, total: 0,
  });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showBankAuthModal, setShowBankAuthModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [adjustmentTarget, setAdjustmentTarget] = useState<VacatairePayrollEntry | null>(null);

  const loadCampuses = useCallback(async () => {
    try {
      const tokens = useAuthStore.getState().tokens;
      const user = useAuthStore.getState().user;
      const tenantId = user?.tenantId;
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/etablissement/campuses`, {
        headers: {
          'Authorization': `Bearer ${tokens?.accessToken}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantId || '',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const active = data.filter((c: Campus) => c.isActive);
        setCampuses(active);
        if (active.length === 1) setSelectedCampusId(active[0].id);
      }
    } catch (err) { console.error('Error loading campuses:', err); }
  }, []);

  useEffect(() => { loadCampuses(); }, [loadCampuses]);

  // Load bank authorization status for the current month
  const loadAuthStatus = useCallback(async () => {
    try {
      const auths = await getVacataireBankAuthorizationHistory(selectedMonth, selectedYear);
      // Group by campus and get latest version per campus
      const byCampus: Record<string, VacataireBankAuthorization> = {};
      auths.forEach((auth) => {
        if (!auth.campusId) return;
        if (!byCampus[auth.campusId] || auth.version > byCampus[auth.campusId].version) {
          byCampus[auth.campusId] = auth;
        }
      });
      const latestAuths = Object.values(byCampus);
      const paid = latestAuths.filter((a) => a.isPaid).length;
      const pending = latestAuths.filter((a) => !a.isPaid).length;
      setAuthSummary({ paid, pending, total: latestAuths.length });
    } catch (err) {
      console.error('Error loading auth status:', err);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => { loadAuthStatus(); }, [loadAuthStatus]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getVacatairePayrollTable(selectedMonth, selectedYear, search || undefined, selectedCampusId || undefined);
      setEntries(result.data);
      setSummary(result.summary);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally { setLoading(false); }
  }, [selectedMonth, selectedYear, search, selectedCampusId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="flex flex-col h-full gap-3">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paie Personnel Vacataire</h1>
        <p className="text-gray-500 text-sm mt-0.5">Gestion mensuelle de la paie des enseignants vacataires (base sur les seances effectuees)</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-2.5">
        <div className="flex flex-col lg:flex-row gap-2 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm">
              {MONTH_NAMES.map((name, idx) => (<option key={idx} value={idx + 1}>{name}</option>))}
            </select>
            <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm">
              {YEARS.map((yr) => (<option key={yr} value={yr}>{yr}</option>))}
            </select>
            <select value={selectedCampusId} onChange={(e) => setSelectedCampusId(e.target.value)}
              className="px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm min-w-[160px]">
              <option value="">Tous les campuses</option>
              {campuses.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher par nom, prenom..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            {canGenerateBankAuth && (
              <button
                onClick={() => setShowBankAuthModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm transition-colors"
              >
                <CreditCard size={16} />
                Ordre de Virement
              </button>
            )}
            <button
              onClick={() => setShowHistoryModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm transition-colors"
            >
              <History size={16} />
              Historique
            </button>
            {canPayVacataires && (
              <button
                onClick={() => setShowConfirmModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 text-sm transition-colors"
              >
                <CheckCheck size={16} />
                Confirmation Paiement
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-2.5">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-blue-600 font-medium">Total Vacataires</p>
              <p className="text-lg font-bold text-blue-700">{summary.totalVacataires}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 p-2.5">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
              <Banknote className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-green-600 font-medium">Montant Net Total</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(summary.totalAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100 p-2.5">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <FileText className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-orange-600 font-medium">Virements En Attente</p>
              <p className="text-lg font-bold text-orange-700">{authSummary.pending}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-100 p-2.5">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-emerald-600 font-medium">Virements Payes</p>
              <p className="text-lg font-bold text-emerald-700">{authSummary.paid}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Main Table Card */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                <Clock className="h-4 w-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-white">Tableau de Paie - {MONTH_NAMES[selectedMonth - 1]} {selectedYear}</h2>
                <p className="text-emerald-100 text-xs">{entries.length} membres du personnel</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-white/80 text-xs">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                {summary.pendingCount} en attente
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                {summary.paidCount} payes
              </span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-16">
              <Users className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun vacataire trouve</h3>
              <p className="mt-1 text-sm text-gray-500">
                Aucun enseignant vacataire avec des seances completees pour cette periode.
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">NOM</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">PRENOM</th>
                  {!selectedCampusId && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">CAMPUS</th>}
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">CIN</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">RIB</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">SEANCES</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">HEURES</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">TAUX/H</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">AJUSTEMENTS</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">MONTANT NET</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map((entry, idx) => (
                  <tr key={`${entry.userId}-${entry.campusId || idx}`} className="hover:bg-emerald-50/50 transition-colors">
                    <td className="px-3 py-2.5 font-medium text-gray-900">{entry.lastName}</td>
                    <td className="px-3 py-2.5 text-gray-600">{entry.firstName}</td>
                    {!selectedCampusId && (
                      <td className="px-3 py-2.5">
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                          {entry.campusName || '-'}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2.5 text-center text-gray-600 text-sm">{entry.cin || '-'}</td>
                    <td className="px-3 py-2.5 text-gray-600 font-mono text-xs">{entry.rib || '-'}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {entry.sessionsCompleted}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center font-medium text-gray-900">
                      {entry.totalHours.toFixed(2)}h
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-600">
                      {formatCurrency(entry.hourlyRate)}/h
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {entry.adjustmentTotal !== 0 ? (
                        <span className={entry.adjustmentTotal > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                          {entry.adjustmentTotal > 0 ? '+' : ''}{formatCurrency(entry.adjustmentTotal)}
                        </span>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`font-bold ${entry.totalAmount > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {formatCurrency(entry.totalAmount)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {canValidateHours && (
                        <button onClick={() => setAdjustmentTarget(entry)}
                          className="px-1.5 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded transition-colors" title="Ajustement">
                          <CreditCard size={13} className="inline mr-0.5" />Ajust.
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modals */}
      {adjustmentTarget && (
        <AdjustmentModal entry={adjustmentTarget} month={selectedMonth} year={selectedYear}
          onClose={() => setAdjustmentTarget(null)} onSuccess={() => { setAdjustmentTarget(null); fetchData(); }} />
      )}
      {showBankAuthModal && (
        <BankAuthorizationModal
          month={selectedMonth}
          year={selectedYear}
          entries={entries}
          campuses={campuses}
          selectedCampusId={selectedCampusId}
          onClose={() => { setShowBankAuthModal(false); loadAuthStatus(); }}
        />
      )}
      {showHistoryModal && (
        <AuthorizationHistoryModal
          canPayVacataires={canPayVacataires}
          onClose={() => { setShowHistoryModal(false); fetchData(); loadAuthStatus(); }}
        />
      )}
      {showConfirmModal && (
        <BulkConfirmModal
          month={selectedMonth}
          year={selectedYear}
          campuses={campuses}
          onClose={() => setShowConfirmModal(false)}
          onSuccess={() => { fetchData(); loadAuthStatus(); }}
        />
      )}
    </div>
  );
}
